import { useMutation, useQueryClient } from 'react-query';
import { CountryCodeObject, Dac7FormDataTypes, Dac7Payload } from '@olxeu-a2b/ui/typings/dac7';
import { useMemo, useState } from 'react';
import DeliverySDK from '@olxeu-a2b/core/dist/src/api/DeliverySDK';
import countries from '@olxeu-a2b/ui/src/pages/orders/components/dac7/utils/countriesList';
import { sdkFactory } from '../../../../extras';

// Convert Residency labels to country codes.
const sanitizeResidencies = (residenciesLabels: CountryCodeObject[]) => {
  if (residenciesLabels.length === 0) {
    return [];
  }
  return residenciesLabels.map(label => ({
    countryCode: countries.find(item => item.label === label.countryCode)?.id,
  }));
};

// remove props not used in the backend logic: type, termsAndConditions
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formDataToPayload = ({
  type,
  termsAndConditions,
  residencies,
  ...rest
}: Dac7FormDataTypes): Dac7Payload => ({
  residencies: sanitizeResidencies(residencies),
  ...rest,
});

const postDac7Data = (sdk: DeliverySDK) => (data: Dac7FormDataTypes) => {
  const dac7Payload = formDataToPayload(data);

  if (data.type === 'PRIVATE') {
    return sdk.postPrivateSellerDac7DeclarationDataV1(
      dac7Payload as Parameters<typeof sdk.postPrivateSellerDac7DeclarationDataV1>[0],
    );
  }
  if (data.type === 'BUSINESS') {
    return sdk.postBusinessSellerDac7DeclarationDataV1(
      dac7Payload as unknown as Parameters<typeof sdk.postBusinessSellerDac7DeclarationDataV1>[0],
    );
  }
  return sdk.postSoleTraderSellerDac7DeclarationDataV1(
    dac7Payload as Parameters<typeof sdk.postSoleTraderSellerDac7DeclarationDataV1>[0],
  );
};

export default function useMutateDac7Data() {
  const queryClient = useQueryClient();
  const sdk = useMemo(() => sdkFactory(), []);
  const [successResult, setSuccessResult] = useState<Record<string, any>>(undefined);
  const [error, setError] = useState(null);

  const { mutate, isLoading } = useMutation(postDac7Data(sdk), {
    onSuccess: result => {
      setSuccessResult(result);
    },
    onError: err => {
      setError(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries('getDac7Data');
    },
  });

  return {
    mutate,
    isLoading,
    successResult,
    error,
  };
}
